import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PetSyncLogo from '../../assets/Images/petsync-logo.png';
import PetParentApk from '../../assets/Apps/PetParent.apk';

const PetDownload: React.FC = () => {
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'loading' | 'downloading' | 'success'>('idle');

  // Inject professional fonts directly into the head
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@900&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const initDownload = () => {
    setDownloadStatus('loading');
    // Simulate secure handshake
    setTimeout(() => {
      setDownloadStatus('downloading');
      // Simulate file fetching
      setTimeout(() => {
        setDownloadStatus('success');
        const link = document.createElement('a');
        link.href = PetParentApk;
        link.download = 'PetParent.apk';
        link.click();
      }, 2400);
    }, 1600);
  };

  const features = [
    { title: 'Instant telehealth', description: 'Consult vets & specialists on demand with a secure in-app workflow.', color: 'from-[#2563eb] to-[#60a5fa]' },
    { title: 'Pet health vault', description: 'Save vaccinations, prescriptions, and medical records in one place.', color: 'from-[#f97316] to-[#fb923c]' },
    { title: 'Smart reminders', description: 'Never miss appointments, medications, or pet care milestones.', color: 'from-[#14b8a6] to-[#2dd4bf]' },
  ];

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#fdfafb] p-6 selection:bg-[#E37383]/20">
      
      {/* Cinematic Background Blobs */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <motion.div 
          animate={{ x: [0, 80, 0], y: [0, 40, 0], rotate: [0, 20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[15%] -right-[10%] w-[650px] h-[650px] rounded-full bg-gradient-to-br from-[#ffeef0] to-[#fff5f6] blur-[120px] opacity-70"
        />
        <motion.div 
          animate={{ x: [0, -60, 0], y: [0, -90, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-[10%] -left-[10%] w-[550px] h-[550px] rounded-full bg-gradient-to-tr from-[#f2ebff] to-[#f9f5ff] blur-[120px] opacity-70"
        />
      </div>

      {/* Main Glassmorphic Card */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
        className="relative w-full max-w-[480px] bg-white/60 backdrop-blur-3xl border border-white/80 rounded-[48px] shadow-[0_40px_100px_rgba(0,0,0,0.05)] p-12 text-center"
      >
        {/* Subtle Shimmer Animation */}
        <div className="absolute inset-0 rounded-[48px] overflow-hidden pointer-events-none opacity-40">
          <motion.div 
            animate={{ left: ['-100%', '200%'] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white to-transparent skew-x-[-30deg]"
          />
        </div>

        {/* Logo with Soft Glow */}
        <div className="relative mx-auto w-24 h-24 mb-8">
            <div className="absolute inset-0 bg-[#E37383]/15 blur-2xl rounded-full scale-125" />
            <motion.div 
              whileHover={{ scale: 1.05, rotate: 2 }}
              className="relative w-full h-full bg-white rounded-[30px] flex items-center justify-center shadow-sm border border-gray-100 p-5"
            >
              <img src={PetSyncLogo} alt="PetSync360" className="w-full h-full object-contain" />
            </motion.div>
        </div>

        {/* Professional Typography Section */}
        {/* <h1 className="font-['Inter'] text-[2.5rem] font-[900] tracking-tighter leading-[0.95] mb-4 text-[#1a1a2e]">
          Pet<span className="text-[#E37383]">Sync</span>360
        </h1> */}
        <p className="font-['Plus_Jakarta_Sans'] text-[#64748b] text-[0.95rem] leading-relaxed mb-10 max-w-[300px] mx-auto font-medium">
          Comprehensive AI-driven care for the modern pet parent.
        </p>

        {/* Feature Grid - Professional Icons */}
        <div className="grid grid-cols-2 gap-4 mb-10">
          {[
            { label: 'Teleconsult', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
            { label: 'Pet Retail', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
            { label: 'Secure Pay', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
            { label: 'PetCare Service', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' }
          ].map((item, i) => (
            <motion.div 
              key={item.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + (i * 0.1) }}
              className="flex flex-col items-center gap-2 p-4 rounded-[24px] bg-white/40 border border-white/60 hover:bg-white/80 transition-all duration-300 group"
            >
              <svg className="w-5 h-5 text-[#E37383] group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
              </svg>
              <span className="font-['Plus_Jakarta_Sans'] text-[0.7rem] font-bold text-[#475569] tracking-widest uppercase">{item.label}</span>
            </motion.div>
          ))}
        </div>

        {/* High-Performance Action Button */}
        <div className="relative group">
          <button 
            disabled={downloadStatus !== 'idle'}
            onClick={initDownload}
            className={`relative w-full py-5 rounded-[24px] font-['Plus_Jakarta_Sans'] text-[1.1rem] font-bold flex items-center justify-center gap-3 overflow-hidden transition-all duration-500
              ${downloadStatus === 'success' ? 'bg-[#10b981] shadow-[0_20px_40px_rgba(16,185,129,0.25)]' : 'bg-[#E37383] text-white shadow-[0_20px_40px_rgba(227,115,131,0.2)] hover:shadow-[0_25px_50px_rgba(227,115,131,0.3)] active:scale-[0.96]'}
            `}
          >
            <span className="relative z-10">
              {downloadStatus === 'idle' && 'Download Android App'}
              {downloadStatus === 'loading' && 'Handshaking...'}
              {downloadStatus === 'downloading' && 'Fetching APK...'}
              {downloadStatus === 'success' && 'Download Started!'}
            </span>
            
            <svg className={`w-6 h-6 z-10 transition-transform duration-300 ${downloadStatus === 'downloading' ? 'animate-bounce' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              {downloadStatus === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              )}
            </svg>

            {/* Internal Loading Progress Overlay */}
            <AnimatePresence>
                {downloadStatus === 'downloading' && (
                <motion.div 
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                />
                )}
            </AnimatePresence>
          </button>
        </div>

        {/* Post-Button Info */}
        <div className="mt-6 flex flex-col items-center gap-1">
            <span className="font-['Plus_Jakarta_Sans'] text-[0.8rem] text-[#94a3b8] font-bold">v1.0.2 • 24.8 MB</span>
            <div className="flex items-center gap-2 text-[0.65rem] text-[#10b981] font-bold tracking-[0.2em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse"/>
                End-to-End Encrypted
            </div>
        </div>

        <p className="mt-12 font-['Plus_Jakarta_Sans'] text-[0.7rem] text-[#cbd5e1] font-semibold tracking-wider">
          © 2026 PETSYNC360 TECHNOLOGIES
        </p>
      </motion.div>

      {/* Professional Fullscreen Handshake Loader */}
      <AnimatePresence>
        {downloadStatus === 'loading' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/60 backdrop-blur-3xl z-[2000] flex flex-col items-center justify-center"
          >
            <div className="relative w-24 h-24">
                <div className="absolute inset-0 border-[6px] border-[#E37383]/10 rounded-full" />
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 border-[6px] border-transparent border-t-[#E37383] rounded-full"
                />
            </div>
            <motion.p 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-8 font-['Plus_Jakarta_Sans'] font-bold text-[#1e293b] tracking-tight"
            >
              Verifying secure download link...
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PetDownload;